import React, { useState, useRef, useEffect } from 'react';
import { api } from '../context/AuthContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Camera,
  Loader2,
  RefreshCw,
  Send,
  Navigation,
  CheckCircle,
  AlertCircle,
  ClipboardList,
  FileDown,
} from 'lucide-react';
import { toast } from 'sonner';

const LaporanKerjaPage = () => {
  const { user } = useAuth();
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const [reports, setReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(true);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [stampedPhoto, setStampedPhoto] = useState(null);
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [deskripsi, setDeskripsi] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sites, setSites] = useState([]);
  const [exportSiteId, setExportSiteId] = useState('all');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchReports();
    getLocation();
    if (['super_admin', 'hr', 'manager'].includes(user?.role)) {
      api.get('/sites').then((res) => setSites(res.data)).catch(() => {});
    }
    return () => stopCamera();
  }, []);

  const fetchReports = async () => {
    try {
      const res = await api.get('/work-reports');
      setReports(res.data);
    } catch (error) {
      console.error('Failed to fetch work reports:', error);
    } finally {
      setLoadingReports(false);
    }
  };

  const getLocation = () => {
    setGettingLocation(true);
    setLocationError(null);

    if (!navigator.geolocation) {
      setLocationError('Geolocation tidak didukung browser');
      setGettingLocation(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
        setGettingLocation(false);
      },
      (error) => {
        let message = 'Gagal mendapatkan lokasi';
        if (error.code === error.PERMISSION_DENIED) message = 'Izin lokasi ditolak';
        else if (error.code === error.POSITION_UNAVAILABLE) message = 'Lokasi tidak tersedia';
        else if (error.code === error.TIMEOUT) message = 'Timeout lokasi';
        setLocationError(message);
        setGettingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const startCamera = async () => {
    setCameraLoading(true);

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      toast.error('Browser tidak mendukung akses kamera');
      setCameraLoading(false);
      return;
    }

    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false,
      });

      streamRef.current = mediaStream;

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.onloadedmetadata = async () => {
          try {
            await videoRef.current.play();
            setCameraActive(true);
            setCameraLoading(false);
          } catch (playError) {
            toast.error('Gagal memutar video');
            setCameraLoading(false);
          }
        };
      }
    } catch (error) {
      console.error('Camera error:', error);
      setCameraLoading(false);
      toast.error(error.name === 'NotAllowedError' ? 'Izin kamera ditolak' : 'Gagal akses kamera');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraActive(false);
  };

  const capturePhoto = () => {
    if (!videoRef.current) return;
    if (!location) {
      toast.error('Lokasi belum didapatkan, tunggu sebentar');
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(videoRef.current, 0, 0);

    // Stamp date/time + location onto the photo
    const now = new Date();
    const dateStr = now.toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' });
    const timeStr = now.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    const coordStr = `${location.latitude.toFixed(5)}, ${location.longitude.toFixed(5)}`;

    const barHeight = Math.max(50, canvas.height * 0.14);
    ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
    ctx.fillRect(0, canvas.height - barHeight, canvas.width, barHeight);

    const fontSize = Math.max(12, Math.round(canvas.width * 0.032));
    ctx.fillStyle = '#ffffff';
    ctx.font = `bold ${fontSize}px sans-serif`;
    ctx.textBaseline = 'middle';
    ctx.fillText(`${dateStr}  ${timeStr}`, 10, canvas.height - barHeight * 0.62);
    ctx.font = `${fontSize * 0.85}px sans-serif`;
    ctx.fillText(coordStr, 10, canvas.height - barHeight * 0.24);

    const photoData = canvas.toDataURL('image/jpeg', 0.75);
    setStampedPhoto(photoData);
    stopCamera();
  };

  const retakePhoto = () => {
    setStampedPhoto(null);
    startCamera();
  };

  const handleSubmit = async () => {
    if (!stampedPhoto) {
      toast.error('Silakan ambil foto terlebih dahulu');
      return;
    }
    if (!deskripsi.trim()) {
      toast.error('Deskripsi wajib diisi');
      return;
    }
    if (!location) {
      toast.error('Lokasi belum didapatkan');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/work-reports', {
        foto_url: stampedPhoto,
        deskripsi: deskripsi.trim(),
        latitude: location.latitude,
        longitude: location.longitude,
      });
      toast.success('Laporan kerja berhasil dikirim');
      setStampedPhoto(null);
      setDeskripsi('');
      fetchReports();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal mengirim laporan');
    } finally {
      setSubmitting(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.get('/work-reports/export', {
        params: exportSiteId !== 'all' ? { site_id: exportSiteId } : {},
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `laporan-kerja-${new Date().toISOString().slice(0, 10)}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Laporan berhasil diekspor');
    } catch (error) {
      toast.error('Gagal mengekspor laporan');
    } finally {
      setExporting(false);
    }
  };

  const isSelfView = !['super_admin', 'hr', 'manager'].includes(user?.role);

  return (
    <div className="space-y-6" data-testid="work-report-page">
      <div>
        <h1 className="text-2xl font-bold">Laporan Kerja</h1>
        <p className="text-muted-foreground text-sm mt-1">Foto bukti kerja dengan stempel jam & lokasi otomatis</p>
      </div>

      {user?.employee_id ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Camera className="w-5 h-5 text-primary" />Buat Laporan
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="relative w-full max-w-[360px] mx-auto" style={{ aspectRatio: '4/3' }}>
              <div className="absolute inset-0 bg-slate-900 rounded-lg overflow-hidden">
                {!cameraActive && !stampedPhoto && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
                    <Camera className="w-10 h-10 mb-3 opacity-50" />
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={startCamera}
                      disabled={cameraLoading}
                      data-testid="open-camera-btn"
                    >
                      {cameraLoading ? (
                        <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Membuka...</>
                      ) : (
                        'Buka Kamera'
                      )}
                    </Button>
                  </div>
                )}

                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  style={{ display: cameraActive && !stampedPhoto ? 'block' : 'none' }}
                  className="w-full h-full object-cover"
                />

                {cameraActive && !stampedPhoto && (
                  <div className="absolute bottom-2 left-0 right-0 flex justify-center">
                    <Button size="sm" onClick={capturePhoto} data-testid="capture-photo-btn">
                      <Camera className="w-4 h-4 mr-1" />Ambil Foto
                    </Button>
                  </div>
                )}

                {stampedPhoto && (
                  <>
                    <img src={stampedPhoto} alt="Laporan" className="w-full h-full object-cover" />
                    <div className="absolute top-2 right-2">
                      <Button size="icon" variant="secondary" className="h-7 w-7" onClick={retakePhoto} data-testid="retake-photo-btn">
                        <RefreshCw className="w-3 h-3" />
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="text-sm">
              {gettingLocation ? (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />Mendapatkan lokasi...
                </div>
              ) : locationError ? (
                <div className="flex items-center justify-between">
                  <span className="text-red-500 text-xs">{locationError}</span>
                  <Button size="sm" variant="ghost" onClick={getLocation}><RefreshCw className="w-3 h-3" /></Button>
                </div>
              ) : location ? (
                <div className="flex items-center gap-2 text-green-600" data-testid="location-status">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-xs">{location.latitude.toFixed(5)}, {location.longitude.toFixed(5)}</span>
                </div>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label>Deskripsi</Label>
              <Textarea
                value={deskripsi}
                onChange={(e) => setDeskripsi(e.target.value)}
                placeholder="Jelaskan pekerjaan yang dilakukan..."
                rows={3}
                data-testid="description-input"
              />
            </div>

            <Button
              className="w-full"
              onClick={handleSubmit}
              disabled={submitting || !stampedPhoto || !deskripsi.trim()}
              data-testid="submit-report-btn"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Mengirim...</>
              ) : (
                <><Send className="w-4 h-4 mr-2" />Kirim Laporan</>
              )}
            </Button>
          </CardContent>
        </Card>
      ) : !isSelfView ? null : (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Akun belum terhubung dengan data karyawan. Hubungi HR.</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <CardTitle className="text-lg flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-primary" />
              {isSelfView ? 'Riwayat Laporan Saya' : 'Semua Laporan Kerja'}
            </CardTitle>
            {!isSelfView && (
              <div className="flex items-center gap-2" data-testid="export-report-controls">
                <Select value={exportSiteId} onValueChange={setExportSiteId}>
                  <SelectTrigger className="w-[160px]" data-testid="export-site-select">
                    <SelectValue placeholder="Semua Site" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Semua Site</SelectItem>
                    {sites.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.nama}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button size="sm" variant="outline" onClick={handleExport} disabled={exporting} data-testid="export-report-btn">
                  {exporting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileDown className="w-4 h-4 mr-1" />}
                  Export Word
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loadingReports ? (
            <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
          ) : reports.length === 0 ? (
            <p className="text-center text-muted-foreground py-6">Belum ada laporan kerja</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="report-list">
              {reports.map((r) => (
                <div key={r.id} className="border rounded-lg overflow-hidden" data-testid={`report-item-${r.id}`}>
                  <img src={r.foto_url} alt="Laporan" className="w-full h-40 object-cover" />
                  <div className="p-3 space-y-1">
                    {!isSelfView && <p className="text-xs font-semibold text-primary">{r.employee_nama}</p>}
                    <p className="text-sm">{r.deskripsi}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(r.created_at).toLocaleString('id-ID')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LaporanKerjaPage;
